/**
 * LAS PIEZAS COMUNES DE LAS GRÁFICAS (2026-10-03).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ EXISTE ESTE ARCHIVO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Las gráficas de «Todas las sucursales» salieron mejor que las de Reportes y
 * Estadísticas, y el dueño lo dijo: «siento que estas gráficas fueron mucho
 * mejor que las que tenemos en negocios y sucursales individualmente». Lo que
 * las hacía mejores no era el color, era la lectura:
 *
 *   · un GLOBO que dice de qué día se trata y el detalle, no solo un número;
 *   · la LÍNEA DE ABAJO que ya dice la conclusión («el día más fuerte…»);
 *   · los períodos largos AGRUPADOS por semana o por mes, en vez de noventa
 *     barras de un píxel;
 *   · el MAPA DE CALOR, que junta el día y la hora.
 *
 * Aquí viven esas piezas para que todas las pantallas las usen iguales: si
 * mañana cambia cómo se ve un globo, cambia en todas a la vez.
 */

import type { ReactNode } from "react";
import { Flame } from "lucide-react";
import { Tarjeta, TituloSeccion, cx } from "@/components/ui";
import { formatFecha, formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";

// =============================================================================
//  EL GLOBO
// =============================================================================

/**
 * Dónde se abre el globo de una columna, por tercios.
 *
 * ⚠️ No solo las puntas: con 30 barras en un celular, el globo centrado del
 * día 27 se salía por la derecha y ensanchaba la página aunque estuviera
 * oculto (lo encontró la revisión de «Todas las sucursales», 2026-10-02).
 */
export function posicionDelGlobo(i: number, total: number): string {
  if (i < total / 3) return "left-0";
  if (i >= (total * 2) / 3) return "right-0";
  return "left-1/2 -translate-x-1/2";
}

export type FilaDelGlobo = {
  /** El color de la serie: un punto al lado del nombre. */
  color?: string;
  nombre: string;
  valor: string;
};

/**
 * El globo oscuro de las gráficas: un título (el día, la hora, el mes), las
 * filas con su valor y, si hay varias, el total separado por una raya.
 *
 * Va dentro de un elemento `group relative`: aparece al pasar el ratón por
 * encima o al tocar en el celular.
 */
export function GloboDeGrafica({
  titulo,
  filas,
  total,
  nota,
  posicion,
  className,
}: {
  titulo: ReactNode;
  filas: FilaDelGlobo[];
  total?: { nombre: string; valor: string };
  /** Una línea suelta debajo, más tenue: «3 ventas», «Todavía no ha llegado». */
  nota?: string;
  /** Lo de `posicionDelGlobo`. */
  posicion: string;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "pointer-events-none absolute bottom-full z-10 mb-1.5 min-w-40",
        "rounded-[var(--radius-control)] bg-ink px-2.5 py-2 text-[11px] text-bg",
        "opacity-0 shadow-[var(--shadow-pop)] transition-opacity group-hover:opacity-100",
        posicion,
        className,
      )}
    >
      <p className="mb-1 whitespace-nowrap font-semibold first-letter:uppercase">{titulo}</p>
      {filas.map((f) => (
        <p key={f.nombre} className="flex items-center justify-between gap-3 whitespace-nowrap">
          <span className="flex min-w-0 items-center gap-1.5">
            {f.color && (
              <span aria-hidden className="inline-block size-2 shrink-0 rounded-full" style={{ backgroundColor: f.color }} />
            )}
            <span className="truncate opacity-80">{f.nombre}</span>
          </span>
          <span className="tabular">{f.valor}</span>
        </p>
      ))}
      {total && (
        <p className="mt-1 flex justify-between gap-3 whitespace-nowrap border-t border-bg/20 pt-1 font-semibold">
          <span>{total.nombre}</span>
          <span className="tabular">{total.valor}</span>
        </p>
      )}
      {nota && <p className="mt-0.5 whitespace-nowrap opacity-70">{nota}</p>}
    </div>
  );
}

// =============================================================================
//  LA LÍNEA DE ABAJO: LA CONCLUSIÓN
// =============================================================================

/**
 * «El día de mayor venta: jueves 24 sept. · $1.206». La frase que el dueño lee
 * sin mirar las barras una por una.
 */
export function LineaDestacada({
  icono,
  texto,
  resaltado,
  valor,
}: {
  icono?: ReactNode;
  texto: string;
  resaltado: string;
  valor?: string;
}) {
  return (
    <p className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-ink-soft">
      <span className="text-accent">{icono ?? <Flame size={14} />}</span>
      {texto}: <strong className="font-semibold text-ink first-letter:uppercase">{resaltado}</strong>
      {valor && <span className="tabular text-muted">· {valor}</span>}
    </p>
  );
}

// =============================================================================
//  LOS DÍAS, POR DÍA, SEMANA O MES
// =============================================================================

export type ModoDePeriodo = "dia" | "semana" | "mes";
export type GrupoDeDias = { clave: string; desde: string; hasta: string; indices: number[] };

/**
 * Los días juntos por día, por semana (lunes a domingo) o por mes.
 *
 * Hasta 45 días va por día; hasta 200, por semana; más, por mes. Noventa
 * columnas de dos píxeles no se leen, y el globo de cada una tapa a las
 * vecinas.
 */
export function agruparPorPeriodo(fechas: string[]): { modo: ModoDePeriodo; grupos: GrupoDeDias[] } {
  const modo: ModoDePeriodo = fechas.length <= 45 ? "dia" : fechas.length <= 200 ? "semana" : "mes";
  const grupos: GrupoDeDias[] = [];
  fechas.forEach((fecha, i) => {
    let clave = fecha;
    if (modo === "semana") {
      const f = new Date(`${fecha}T12:00:00`);
      // El lunes de su semana: getDay() da 0 el domingo.
      f.setDate(f.getDate() - ((f.getDay() + 6) % 7));
      clave = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}`;
    } else if (modo === "mes") {
      clave = fecha.slice(0, 7);
    }
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.clave === clave) {
      ultimo.hasta = fecha;
      ultimo.indices.push(i);
    } else {
      grupos.push({ clave, desde: fecha, hasta: fecha, indices: [i] });
    }
  });
  return { modo, grupos };
}

/** «jueves, 24 sept.», «Del 7 sept. al 13 sept.» o «septiembre de 2026». */
export function tituloDelGrupo(modo: ModoDePeriodo, g: Pick<GrupoDeDias, "desde" | "hasta">): string {
  const f = (iso: string, o: Intl.DateTimeFormatOptions) => formatFecha(`${iso}T12:00:00`, o);
  if (modo === "mes") return f(g.desde, { month: "long", year: "numeric" });
  if (modo === "semana") {
    return `Del ${f(g.desde, { day: "numeric", month: "short" })} al ${f(g.hasta, { day: "numeric", month: "short" })}`;
  }
  return f(g.desde, { weekday: "long", day: "numeric", month: "short" });
}

/** La etiqueta corta bajo la columna. */
export function etiquetaDelGrupo(modo: ModoDePeriodo, g: Pick<GrupoDeDias, "desde">, cuantos: number): string {
  const f = (o: Intl.DateTimeFormatOptions) => formatFecha(`${g.desde}T12:00:00`, o);
  if (modo === "mes") return f({ month: "short" });
  if (modo === "semana") return f({ day: "numeric", month: "short" });
  return cuantos <= 7 ? f({ weekday: "short" }) : f({ day: "numeric" });
}

/** Hoy en la hora del aparato (no en la de Greenwich: a las 9 de la noche en Caracas, `toISOString` ya diría mañana). */
export function hoyIso(): string {
  const a = new Date();
  return `${a.getFullYear()}-${String(a.getMonth() + 1).padStart(2, "0")}-${String(a.getDate()).padStart(2, "0")}`;
}

// =============================================================================
//  EL MAPA DE CALOR
// =============================================================================

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

export type CasillaDelMapa = { dia: number; hora: number; total: string; ventas: number };

/**
 * Lo vendido en cada hora de cada día de la semana.
 *
 * Las horas y los días por separado no dicen que «el sábado a las 10» es el
 * momento más fuerte de la semana; juntos, sí. Es lo que decide cuántas
 * personas poner en el mostrador. `dia` 0 = lunes.
 */
export function MapaDeCalor({
  casillas,
  formato,
  titulo = "Ventas por día y hora",
  descripcion = "Cada casilla representa una hora de un día de la semana, acumulada durante el período. A mayor intensidad del color, mayores ventas.",
}: {
  casillas: CasillaDelMapa[];
  formato: FormatoMoneda;
  titulo?: string;
  descripcion?: string;
}) {
  if (casillas.length === 0) return null;

  const horas = casillas.map((c) => c.hora);
  const primera = Math.min(...horas);
  const ultima = Math.max(...horas);
  const columnas = Array.from({ length: ultima - primera + 1 }, (_, i) => primera + i);
  const de = new Map(casillas.map((c) => [`${c.dia}:${c.hora}`, c]));
  const maximo = Math.max(...casillas.map((c) => toNumber(c.total)), 0);
  const mejor = casillas.reduce((a, c) => (toNumber(c.total) > toNumber(a.total) ? c : a), casillas[0]!);
  const hora = (h: number) => `${String(h).padStart(2, "0")}:00`;

  return (
    <Tarjeta>
      <TituloSeccion titulo={titulo} descripcion={descripcion} />
      <div className="sin-barra -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <table className="w-full min-w-[560px] border-separate border-spacing-[3px] text-[10px]">
          <thead>
            <tr>
              <th className="w-10" />
              {columnas.map((h) => (
                <th key={h} className="tabular font-normal text-faint">
                  {String(h).padStart(2, "0")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DIAS.map((nombre, dia) => (
              <tr key={nombre}>
                <th className="pr-1.5 text-left text-xs font-medium text-muted">{nombre.slice(0, 3)}</th>
                {columnas.map((h) => {
                  const c = de.get(`${dia}:${h}`);
                  const valor = c ? toNumber(c.total) : 0;
                  // Un mínimo de intensidad para que una casilla con ventas no
                  // se confunda con una vacía.
                  const intensidad = maximo > 0 && valor > 0 ? 0.12 + (valor / maximo) * 0.88 : 0;
                  return (
                    <td
                      key={h}
                      title={
                        c
                          ? `${nombre}, ${hora(h)}: ${formatMoney(c.total, formato)} · ${c.ventas} ${c.ventas === 1 ? "venta" : "ventas"}`
                          : `${nombre}, ${hora(h)}: sin ventas`
                      }
                      className={cx(
                        "h-7 rounded-[4px]",
                        valor === 0 && "bg-surface-2",
                        c === mejor && "ring-2 ring-accent ring-offset-1 ring-offset-surface",
                      )}
                      style={
                        valor > 0
                          ? { backgroundColor: `color-mix(in srgb, var(--c-accent) ${Math.round(intensidad * 100)}%, transparent)` }
                          : undefined
                      }
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
        <p className="flex items-center gap-1.5 text-ink-soft">
          <Flame size={14} className="text-accent" />
          Franja de mayor venta: <strong className="text-ink">{DIAS[mejor.dia]} a las {hora(mejor.hora)}</strong>
          <span className="tabular text-muted">· {formatMoney(mejor.total, formato)}</span>
        </p>
        <p className="flex items-center gap-1.5 text-xs text-muted" aria-hidden="true">
          Menos
          {[0.15, 0.4, 0.65, 0.9].map((o) => (
            <span
              key={o}
              className="inline-block size-3 rounded-[3px]"
              style={{ backgroundColor: `color-mix(in srgb, var(--c-accent) ${Math.round(o * 100)}%, transparent)` }}
            />
          ))}
          Más
        </p>
      </div>
    </Tarjeta>
  );
}
